import React, { useContext } from 'react'
import { NavLink, useNavigate } from 'react-router'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'
import { FormProvider, useForm } from 'react-hook-form'
import UserInfoSection from '@Scenes/UserRegistrationScenes/UserRegistrationScene/UserRegistrationForm/UserInfoSection'
import TermsAndCommunicationsSection from '@Scenes/UserRegistrationScenes/UserRegistrationScene/UserRegistrationForm/TermsAndCommunicationsSection'
import SubmitButton from '@App/kit/fields/SubmitButton'
import { ICONS } from '@Const/common'
import {
    userRegistrationFormFields,
    UserRegistrationInput,
    userRegistrationSchema,
    UserRegistrationType,
} from '@Entities/schemas/user/user-registration'
import { zodResolver } from '@hookform/resolvers/zod'
import MailingAddress from '@App/kit/fields/v2/MailingAddress'
import MailingAddressToolTip from '@Components/Tooltips/MailingAddressToolTip'
import Navigation from '@App/navigation'
import ContactInfoInterPhoneSection from '@Components/ContactInfoInterPhoneSection'
import { KYC_ROUTE } from '@App/navigation/route'

const UserRegistrationForm = () => {
    const navigate = useNavigate()

    const { popupStore, preloaderStore, uiStore, userStore } = useContext(StoresContext)

    const idvOnSubmit = async (data: UserRegistrationType) => {
        preloaderStore.openTransparentPreloader(true)
        try {
            await userStore.createUserAndAttemptIdv(data)
            window.dataLayer.push({ event: 'sign_up_complete' })
            navigate(`/${KYC_ROUTE}?` + userStore.getAu10TixWorkflowLinkParams())
        } catch (e) {
            popupStore.setAlertFromError(e)
        } finally {
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const { firstName, lastName, birthDate, email, termsAndConditions, privacyPolicy, address, phoneNumber } =
        userRegistrationFormFields

    const methods = useForm<UserRegistrationInput, unknown, UserRegistrationType>({
        mode: 'onChange',
        criteriaMode: 'all',
        delayError: 750, // MilliSeconds: this delay is here so email validation doesn't start when user starts typing
        resolver: zodResolver(userRegistrationSchema),
        defaultValues: {
            termsAndConditions: true,
            privacyPolicy: true,
        },
    })

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(idvOnSubmit)}>
                <UserInfoSection firstName={firstName} lastName={lastName} birthday={birthDate} />
                <MailingAddress
                    title="Personal Address"
                    toolTip={<MailingAddressToolTip />}
                    addressFormFields={address}
                />
                <ContactInfoInterPhoneSection email={email} fullPhoneNumber={phoneNumber} />
                <TermsAndCommunicationsSection termsAndConditions={termsAndConditions} privacyPolicy={privacyPolicy} />
                <SubmitButton label="Sign up" />
                <NavLink
                    to={Navigation.login.root()}
                    onClick={() => uiStore.setOnLoginPage(true)}
                    className="close-wrapper"
                >
                    <img src={ICONS.close} alt="close" />
                </NavLink>
            </form>
        </FormProvider>
    )
}

export default observer(UserRegistrationForm)
